// src/api/client.ts
import axios from 'axios';
import { AUTH_STORAGE_KEY, LEGACY_AUTH_STORAGE_KEYS } from '../constants/authStorage'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8000",
})

let sessionExpiredRedirectInProgress = false

function readAuthTokenFromStorage() {
  let raw = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!raw) {
    for (const legacyKey of LEGACY_AUTH_STORAGE_KEYS) {
      const legacyRaw = localStorage.getItem(legacyKey)
      if (legacyRaw) {
        raw = legacyRaw
        break
      }
    }
  }

  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed?.token ? String(parsed.token) : null
  } catch {
    return null
  }
}

function collectErrorText(payload: any): string {
  const parts = [
    payload?.code,
    payload?.detail,
    payload?.message,
    payload?.error,
    payload?.non_field_errors?.[0],
    payload?.messages?.[0]?.message,
    payload?.errors?.[0]?.message,
  ]
  if (Array.isArray(payload?.detail)) {
    for (const item of payload.detail) {
      if (typeof item === 'string') parts.push(item)
      if (item && typeof item === 'object' && typeof item.message === 'string') parts.push(item.message)
    }
  }
  return parts
    .filter((value) => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .toLowerCase()
}

function isSessionConflictSignal(errorText: string) {
  return (
    errorText.includes('session conflict') ||
    errorText.includes('another device') ||
    errorText.includes('logged in on another')
  )
}

function isSessionExpiredSignal(status: number | undefined, errorText: string) {
  if (![401, 403].includes(Number(status || 0))) return false
  return (
    errorText.includes('token_not_valid') ||
    errorText.includes('token has expired') ||
    errorText.includes('given token not valid for any token type') ||
    errorText.includes('authentication credentials were not provided') ||
    errorText.includes('invalid token') ||
    errorText.includes('jwt') && errorText.includes('expired') ||
    (errorText.includes('refresh') && errorText.includes('expired'))
  )
}

function toSafeRedirectPath(path: string) {
  if (!path || !path.startsWith('/') || path.startsWith('//')) {
    return '/admin/dashboard'
  }
  return path
}

function shouldHandleAsSessionExpired(error: any) {
  const status = error?.response?.status as number | undefined
  if (![401, 403].includes(Number(status || 0))) return false

  const errorText = collectErrorText(error?.response?.data || {})
  if (isSessionConflictSignal(errorText)) return false
  const explicitExpirySignal = isSessionExpiredSignal(status, errorText)
  if (explicitExpirySignal) return true

  const url = String(error?.config?.url || '').toLowerCase()
  const isAuthFlowRequest =
    url.includes('/api/core/admin/login/start/') ||
    url.includes('/api/core/admin/login/verify-otp/') ||
    url.includes('/api/core/login/') ||
    url.includes('/api/core/forgot-password/') ||
    url.includes('/api/core/reset-password/')
  if (isAuthFlowRequest) return false

  const headerToken = String(error?.config?.headers?.Authorization || '')
  const hasBearerHeader = headerToken.toLowerCase().startsWith('bearer ')
  const hasStoredToken = Boolean(readAuthTokenFromStorage())

  // Generic 403 (permissions/business rules) is not a session-expired condition.
  if (Number(status || 0) === 403) return false

  // For generic 401s on protected calls, treat as expired only when a token exists.
  return Number(status || 0) === 401 && (hasBearerHeader || hasStoredToken)
}

function handleSessionExpiryRedirect() {
  if (sessionExpiredRedirectInProgress) return
  sessionExpiredRedirectInProgress = true

  setAuthToken(null)
  localStorage.removeItem(AUTH_STORAGE_KEY)
  for (const legacyKey of LEGACY_AUTH_STORAGE_KEYS) {
    localStorage.removeItem(legacyKey)
  }
  sessionStorage.clear()

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const redirectPath = toSafeRedirectPath(currentPath)
  const target = `/session-expired?redirect=${encodeURIComponent(redirectPath)}`
  const alreadyOnExpiredView = window.location.pathname === '/session-expired'

  if (import.meta.env.DEV) {
    console.debug('[auth] session-expired redirect', {
      from: currentPath,
      target,
    })
  }

  if (!alreadyOnExpiredView) {
    window.location.assign(target)
  }
}

api.interceptors.request.use((config) => {
  // 1) Prefer header if already set by setAuthToken()
  if (config.headers?.Authorization) return config

  // 2) Otherwise load from our persisted auth object
  let raw = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!raw) {
    for (const legacyKey of LEGACY_AUTH_STORAGE_KEYS) {
      const legacyRaw = localStorage.getItem(legacyKey)
      if (legacyRaw) {
        raw = legacyRaw
        break
      }
    }
  }

  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      const token = parsed?.token
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch {
      // ignore bad JSON
    }
  }
  return config
})

export function setAuthToken(token: string | null) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete api.defaults.headers.common['Authorization']
  }
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (shouldHandleAsSessionExpired(error)) {
      handleSessionExpiryRedirect()
    }
    return Promise.reject(error)
  },
)

export default api
